import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  useTheme
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Period } from '@/store/hr/PlanReportStore';

type Mode = 'week' | 'month' | 'custom';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/** Add days to a YYYY-MM-DD, anchored at noon so it can't slip a day. */
const addDays = (dateStr: string, delta: number): string => {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

/** The ISO Monday of a date's week. Must match the backend's WEEKDAY() bucketing. */
const mondayOf = (dateStr: string): string => {
  const d = new Date(`${dateStr}T12:00:00Z`);
  const isoDow = (d.getUTCDay() + 6) % 7; // 0 = Monday
  return addDays(dateStr, -isoDow);
};

const humanDate = (dateStr: string) =>
  new Date(`${dateStr}T12:00:00`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short'
  });

interface PeriodSelectorProps {
  /** The server's business date, in company tz. Never derive "today" on the client. */
  today?: string;
  onChange: (period: Period) => void;
}

/**
 * The single source of period truth.
 *
 * The API's `to` is EXCLUSIVE (half-open [from, to), matching monthRange() on the
 * backend), while humans read inclusive ranges. This component is the ONLY place
 * that conversion is allowed to happen — every page just consumes {from, to, label}.
 */
const PeriodSelector: React.FC<PeriodSelectorProps> = ({ today, onChange }) => {
  const theme = useTheme();
  const [mode, setMode] = useState<Mode>('month');
  /** Anchor: the Monday of the week, or the 1st of the month. */
  const [anchor, setAnchor] = useState<string>('');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState(''); // inclusive, as shown to the user

  // Seed from the server's date once it arrives.
  useEffect(() => {
    if (anchor || !today) return;
    setAnchor(`${today.slice(0, 7)}-01`);
    setCustomFrom(addDays(today, -6));
    setCustomTo(today);
  }, [today, anchor]);

  const period: Period | null = useMemo(() => {
    if (mode === 'custom') {
      if (!customFrom || !customTo || customFrom > customTo) return null;
      return {
        from: customFrom,
        to: addDays(customTo, 1), // inclusive UI -> exclusive wire
        label: `${humanDate(customFrom)} – ${humanDate(customTo)}`
      };
    }
    if (!anchor) return null;

    if (mode === 'week') {
      const from = mondayOf(anchor);
      const to = addDays(from, 7); // exclusive
      return {
        from,
        to,
        label: `${humanDate(from)} – ${humanDate(addDays(to, -1))}`
      };
    }

    // month
    const [y, m] = anchor.split('-').map(Number);
    const from = `${y}-${String(m).padStart(2, '0')}-01`;
    const nextY = m === 12 ? y + 1 : y;
    const nextM = m === 12 ? 1 : m + 1;
    const to = `${nextY}-${String(nextM).padStart(2, '0')}-01`; // exclusive
    return { from, to, label: `${MONTH_NAMES[m - 1]} ${y}` };
  }, [mode, anchor, customFrom, customTo]);

  useEffect(() => {
    if (period) onChange(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period?.from, period?.to]);

  const step = (delta: number) => {
    if (!anchor) return;
    if (mode === 'week') {
      setAnchor(addDays(mondayOf(anchor), delta * 7));
      return;
    }
    const [y, m] = anchor.split('-').map(Number);
    let year = y;
    let month = m + delta;
    if (month < 1) {
      month = 12;
      year -= 1;
    }
    if (month > 12) {
      month = 1;
      year += 1;
    }
    setAnchor(`${year}-${String(month).padStart(2, '0')}-01`);
  };

  const dateSx = {
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: theme.palette.mode === 'dark' ? 'invert(1)' : 'none'
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_e, v) => v && setMode(v)}
        aria-label="Report period"
      >
        <ToggleButton value="week" sx={{ textTransform: 'none' }}>Week</ToggleButton>
        <ToggleButton value="month" sx={{ textTransform: 'none' }}>Month</ToggleButton>
        <ToggleButton value="custom" sx={{ textTransform: 'none' }}>Custom</ToggleButton>
      </ToggleButtonGroup>

      {mode === 'custom' ? (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField
            type="date"
            label="From"
            size="small"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today }}
            sx={dateSx}
          />
          <TextField
            type="date"
            label="To"
            size="small"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today }}
            sx={dateSx}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <IconButton onClick={() => step(-1)} aria-label="previous period" size="small">
            <ChevronLeftIcon />
          </IconButton>
          <Typography sx={{ fontWeight: 700, minWidth: 170, textAlign: 'center' }}>
            {period?.label || '—'}
          </Typography>
          <IconButton onClick={() => step(1)} aria-label="next period" size="small">
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default PeriodSelector;
